import { createTheme } from '@mui/material';
import { typography } from './typography';
import { colorSet } from './colorSet';
import { styleConstant } from '@constant';

//Custom typography variant start
declare module '@mui/material/styles' {
  interface TypographyVariants {
    subtitle: React.CSSProperties;
    body3: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    subtitle?: React.CSSProperties;
    body3?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle: true;
    body3: true;
    h4: false;
    h5: false;
    h6: false;
    subtitle1: false;
    subtitle2: false;
  }
}
//Custom typography variant end

//Custom palette variant start
declare module '@mui/material/styles' {
  interface Palette {
    point: Palette['primary'];
  }
  interface PaletteOptions {
    point?: PaletteOptions['primary'];
  }
}
//Custom palette variant end

//Custom Button variant start
declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xLarge: true;
  }
  interface ButtonPropsColorOverrides {
    point: true;
  }
}
//Custom Button variant end

const theme = createTheme({
  typography: {
    fontFamily: 'Noto Sans KR, Noto Sans',
    h1: typography.h1,
    h2: typography.h2,
    h3: typography.h3,
    subtitle: typography.subtitle,
    body1: typography.body1,
    body2: typography.body2,
    body3: typography.body3,
    h4: undefined,
    h5: undefined,
    h6: undefined,
    subtitle1: undefined,
    subtitle2: undefined
  },

  palette: {
    primary: {
      light: colorSet.primaryMain,
      main: colorSet.primaryMain,
      dark: colorSet.primaryMain,
      contrastText: colorSet.white
    },
    secondary: {
      light: colorSet.gray400,
      main: colorSet.gray400,
      dark: colorSet.gray400,
      contrastText: colorSet.white
    },
    point: {
      light: colorSet.pointMain,
      main: colorSet.pointMain,
      dark: colorSet.pointMain,
      contrastText: colorSet.white
    },
    error: {
      light: colorSet.errorMain,
      main: colorSet.errorMain,
      dark: colorSet.errorMain,
      contrastText: colorSet.white
    },
    success: {
      light: colorSet.successMain,
      main: colorSet.successMain,
      dark: colorSet.successMain,
      contrastText: colorSet.white
    },
    warning: {
      light: colorSet.warningMain,
      main: colorSet.warningMain,
      dark: colorSet.warningMain,
      contrastText: colorSet.white
    }
  },

  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'p',
          h2: 'p',
          h3: 'p',
          subtitle: 'p',
          body1: 'p',
          body2: 'p',
          body3: 'p'
        },
        color: colorSet.gray600
      }
    },

    MuiButton: {
      defaultProps: {
        size: 'large'
      },
      styleOverrides: {
        root: {
          ...typography.body1,
          fontWeight: 700,
          color: colorSet.white,
          borderRadius: 0,
          boxShadow: 'none',
          // 커스텀 contained point + disabled 처리
          '&.MuiButton-containedPoint.Mui-disabled': {
            backgroundColor: colorSet.gray300,
            color: colorSet.white
          }
        },
        sizeLarge: {
          width: 224,
          height: 44
        },
        sizeMedium: {
          width: 112,
          height: 44
        },
        sizeSmall: {
          width: 89,
          height: 32,
          ...typography.body2
        },
        containedPrimary: {
          backgroundColor: colorSet.primaryMain,
          '&.Mui-disabled': {
            backgroundColor: colorSet.gray300,
            color: colorSet.white
          }
        },
        outlinedPrimary: {
          border: `1px solid ${colorSet.primaryMain}`,
          color: colorSet.primaryMain,
          '&.Mui-disabled': {
            border: `1px solid ${colorSet.gray300}`,
            color: colorSet.gray300
          }
        },
        containedSecondary: {
          backgroundColor: colorSet.gray400,
          '&.Mui-disabled': {
            backgroundColor: colorSet.gray300,
            color: colorSet.white
          }
        },
        outlinedSecondary: {
          border: `1px solid ${colorSet.gray400}`,
          color: colorSet.gray400,
          fontWeight: 'normal',
          '&.Mui-disabled': {
            border: `1px solid ${colorSet.gray300}`,
            color: colorSet.gray300
          }
        },
        textPrimary: {
          color: colorSet.primaryMain
        },
        textSecondary: {
          color: colorSet.gray400
        }
      },
      variants: [
        {
          props: { size: 'xLarge' },
          style: {
            width: 416,
            height: 44
          }
        }
      ]
    },

    //Modal
    MuiModal: {
      styleOverrides: {
        root: {
          zIndex: styleConstant.zIndexModal
        }
      }
    },

    //Select.tsx
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.Mui-disabled': {
            backgroundColor: colorSet.gray200
          }
        }
      }
    },

    //TextInput.tsx, root borderRadius는 Select.tsx에 적용됨
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: 0,
          border: `1px solid ${colorSet.gray300}`,
          borderRadius: '4px'
        },
        notchedOutline: {
          border: 'none',
          '&:hover': {
            border: 'none'
          }
        },
        input: {
          padding: '12px 16px 12px 16px',
          minHeight: 44,
          boxSizing: 'border-box',
          ...typography.body1
        }
      }
    },

    //Checkbox.tsx
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
          marginRight: 8
        }
      }
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0
        }
      }
    },

    //Radio.tsx
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 0,
          marginRight: 8,

          '&.Mui-checked': {
            boxShadow: `0px 2px 4px 0px rgba(115, 103, 240, 0.4)`
          }
        }
      }
    },

    //SearchBar.tsx
    MuiAutocomplete: {
      defaultProps: {
        freeSolo: true,
        fullWidth: true,
        disablePortal: true,
        classes: {
          listbox: 'scroll'
        }
      },
      styleOverrides: {
        root: {
          // search-bar-autocomplete className: SearchBar 및 TagSearchBar 컴포넌트에 적용
          '&.search-bar-autocomplete .MuiOutlinedInput-root': {
            height: 44,
            border: 'none',
            borderRadius: '0px',
            boxShadow: `0px 4px 10px rgba(0, 0, 0, 0.1)`,
            paddingLeft: '12px',
            paddingRight: '12px !important',
            '& svg': { width: 20, height: 20 },
            '& button': {
              padding: 0,
              height: 20,
              width: 20
            },
            '&:hover': {
              border: 'none'
            },
            '&.Mui-focused': {
              border: 'none'
            },
            '&.Mui-error': {
              border: 'none'
            },
            '&.Mui-disabled': {
              border: 'none',
              backgroundColor: 'transparent',
              color: colorSet.gray400
            },
            '& .MuiAutocomplete-input': {
              padding: '0px'
            }
          },
          // search-input-autocomplete className: SearchInput 컴포넌트에 적용
          '&.search-input-autocomplete .MuiOutlinedInput-root': {
            height: 44,
            padding: '8px 16px',
            border: `1px solid ${colorSet.gray300}`,
            borderRadius: '4px',
            boxShadow: 'none',
            '&:hover': {
              border: `1px solid ${colorSet.primaryMain}`
            },
            '&.Mui-focused': {
              border: `1px solid ${colorSet.gray600}`
            },
            '&.Mui-error': {
              border: `1px solid ${colorSet.errorMain}`
            },
            '&.Mui-disabled': {
              border: `1px solid ${colorSet.gray300}`,
              backgroundColor: colorSet.gray200,
              color: colorSet.gray400
            },
            '& .MuiAutocomplete-input': {
              padding: '0px'
            }
          }
        },
        listbox: {
          paddingTop: '6px',
          paddingBottom: '6px'
        },
        option: {
          height: '32px',
          paddingLeft: '20px !important',
          paddingRight: '20px !important',
          '&.Mui-focused': {
            fontWeight: 'bold'
          },
          '&:hover': {
            fontWeight: 'bold'
          }
        }
      }
    }
  }
});

export default theme;
